import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  permissionsFor,
  type AuthPermissions,
  type LoginRequest,
  type SessionUser,
} from "@shared/schemas/auth";
import { onUnauthorized } from "@client/lib/api";
import { fetchMe, postLogin, postLogout } from "@client/lib/authApi";
import {
  ANONYMOUS_PERMISSIONS,
  AuthContext,
  type AuthState,
  type AuthStatus,
} from "@client/lib/authContext";

interface Resolved {
  status: AuthStatus;
  user: SessionUser | null;
  permissions: AuthPermissions;
}

const ANONYMOUS: Resolved = {
  status: "anonymous",
  user: null,
  permissions: ANONYMOUS_PERMISSIONS,
};

/**
 * Holds who is signed in, for the whole app.
 *
 * One `GET /api/auth/me` on boot answers both gates at once — the session and the
 * allow list — because the server re-checks them on every request. Anything other
 * than a clean 200 lands the app in "anonymous"; there is no partial state where
 * the client thinks it knows better than the server.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [resolved, setResolved] = useState<Resolved>({
    status: "loading",
    user: null,
    permissions: ANONYMOUS_PERMISSIONS,
  });

  useEffect(() => {
    let cancelled = false;

    fetchMe()
      .then((me) => {
        if (!cancelled) {
          setResolved({ status: "authenticated", user: me.user, permissions: me.permissions });
        }
      })
      .catch(() => {
        // A 401 is the ordinary case — nobody is signed in. A network failure is
        // treated the same way: without the server's word for it we have no
        // session, and the login page is where that gets resolved.
        if (!cancelled) setResolved(ANONYMOUS);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * A session can end between two clicks — it expires, an administrator revokes
   * the allow-list entry, a colleague signs out in a shared browser — and the
   * next API call comes back 401. `api.ts` announces those; this is what turns
   * one into the login page, once, for every query and mutation in the app.
   *
   * The cache is thrown away with it. Leaving one person's products in memory
   * while the next person signs in is how a cache becomes a data leak.
   */
  useEffect(
    () =>
      onUnauthorized(() => {
        setResolved(ANONYMOUS);
        queryClient.clear();
      }),
    [queryClient],
  );

  const signIn = useCallback(
    async (credentials: LoginRequest) => {
      // Deliberately not caught: the login form maps the error code to a message,
      // and it is the only place that can say anything useful about a failure.
      const { user } = await postLogin(credentials);
      // Whatever the previous occupant of this tab left behind is not this
      // person's to see.
      queryClient.clear();
      // Derived with the same shared function the server uses, so the button this
      // hides and the guard that enforces it cannot disagree.
      setResolved({ status: "authenticated", user, permissions: permissionsFor(user.role) });
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await postLogout();
    } finally {
      // The server always answers 204, but even a failed call must end with the
      // client signed out — leaving someone looking at a session they asked to
      // end is the worse failure.
      setResolved(ANONYMOUS);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(
    () => ({ ...resolved, signIn, signOut }),
    [resolved, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
